import { oc } from "@orpc/contract";
import { z } from "zod";

// Generic "name this thing for me" endpoint. Callers hand over whatever
// objects describe the subject (a workflow graph, a filter set, a row
// selection, an email thread — anything JSON-serializable) plus optional
// context, and get back one short human-facing name from a fast model.
// Deliberately schema-agnostic: the model reads the JSON as-is, so no caller
// needs a bespoke contract just to get a name. Pass `rename` to turn the call
// into a rename — the model improves on the existing name instead of starting
// cold, optionally steered by why the current name is wrong.

/** An arbitrary JSON-serializable subject. Serialized verbatim for the model. */
const SubjectSchema = z.unknown();

/**
 * Cap on the serialized size of all subjects combined. The item count alone
 * bounds nothing — one subject can nest arbitrarily deep — and every accepted
 * request costs an LLM call, so the byte budget is what actually keeps prompt
 * size (and spend) predictable. Roughly 5k tokens of JSON; a name needs far
 * less context than that, so hitting this means the caller should project the
 * fields it actually wants named instead of dumping a whole record.
 */
export const MAX_SUBJECTS_BYTES = 20_000;

export const RenameRequestSchema = z.object({
  /** The thing's existing name. The model must not return it unchanged. */
  currentName: z.string().min(1).max(200),
  /** Why it's being renamed — what's wrong with the current name. */
  reason: z.string().max(1000).optional(),
});

export const SuggestNameInputSchema = z.object({
  /** What kind of thing is being named, e.g. "workflow", "saved view". */
  kind: z.string().min(1).max(100).optional(),
  /** The object(s) describing the thing being named. */
  subjects: z
    .array(SubjectSchema)
    .min(1)
    .max(50)
    .refine(
      (subjects) => JSON.stringify(subjects).length <= MAX_SUBJECTS_BYTES,
      `Subjects serialize to more than ${MAX_SUBJECTS_BYTES} characters — pass only the fields relevant to the name`,
    ),
  /** Free-form extra context — user intent, surrounding page, sibling names. */
  context: z.string().max(4000).optional(),
  /** Set to rename an already-named thing instead of naming from scratch. */
  rename: RenameRequestSchema.optional(),
  /** Names to avoid colliding with (e.g. siblings in the same list). */
  avoid: z.array(z.string().max(200)).max(50).optional(),
  /** Soft target for the name's length in characters. */
  maxLength: z.number().int().min(3).max(200).default(60),
});

export const SuggestNameResponseSchema = z.object({
  name: z.string().min(1),
});

export const aiNamingContracts = {
  suggestName: oc
    .input(SuggestNameInputSchema)
    .output(SuggestNameResponseSchema)
    .route({
      method: "POST",
      summary: "Suggest a name with AI",
      description:
        "Generates one short human-readable name for any described item (workflow, saved view, selection), or improves an existing name when rename details are given.",
    }),
} as const;

export type RenameRequest = z.infer<typeof RenameRequestSchema>;
export type SuggestNameInput = z.infer<typeof SuggestNameInputSchema>;
export type SuggestNameResponse = z.infer<typeof SuggestNameResponseSchema>;
